import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { findRegenerationJob, readRegenerationJob, regenerateContent, RegenerationJob } from '@/services/contentReviewService';

import { RegenerationRequestError } from '@/services/regenerationRequestError';

import { RegenerationObservation } from './useRegenerationQueueObserver';

type Receipt = { requestId: string; jobId?: string };
type View = { phase: 'checking' | 'idle' | 'submitting' | 'unavailable' | 'unconfirmed' | 'rejected' | RegenerationJob['status']; message?: string; paused?: boolean };
const POLL_WINDOW_MS = 300000;

function pause(signal: AbortSignal) {
  return new Promise<void>(resolve => {
    const finish = () => { clearTimeout(timer); signal.removeEventListener('abort', finish); resolve(); };
    const timer = setTimeout(finish, 3000);
    signal.addEventListener('abort', finish, { once: true });
    if (signal.aborted) finish();
  });
}

/** The browser observes the durable job; losing observation never means generation failed. */
export function RegenerationControl({ contentId, siteId, userId, disabled, onComplete, onObserve }: {
  contentId: string; siteId: string; userId: string; disabled?: boolean; onComplete: () => void; onObserve?: (observation: RegenerationObservation) => void;
}) {
  const storageKey = `content-review-regeneration:${userId}:${siteId}:${contentId}`;
  const [view, setView] = useState<View>({ phase: 'checking' });
  const [jobId, setJobId] = useState<string>();
  const controller = useRef<AbortController>();
  const busy = useRef(false);
  // A discovered running job still owes this panel a refresh after observation reconnects.
  const awaitingCompletion = useRef<string>();
  const completion = useRef(onComplete);
  completion.current = onComplete;

  function receipt(): Receipt | null {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (!value || typeof value.requestId !== 'string' || (value.jobId !== undefined && typeof value.jobId !== 'string')) {
      throw new Error('Saved regeneration request is invalid; status must be checked before submitting again.');
    }
    return value;
  }

  function assertCurrentReceipt(expectedRequestId: string | null) {
    if ((receipt()?.requestId ?? null) !== expectedRequestId) {
      throw new Error('A newer regeneration request was saved in another tab. Check the existing request to reconnect.');
    }
  }
  function save(value: Receipt, expectedRequestId: string | null) {
    assertCurrentReceipt(expectedRequestId);
    localStorage.setItem(storageKey, JSON.stringify(value));
  }
  function clearReceipt(requestId: string | null) {
    // A discovered job without a receipt owns no persisted request to remove.
    if (requestId === null) return;
    assertCurrentReceipt(requestId);
    localStorage.removeItem(storageKey);
  }

  async function submit(requestId: string, signal: AbortSignal) {
    try { return await regenerateContent('product_aeo', contentId, { requestId, signal }); }
    catch (error) {
      if (!(error instanceof RegenerationRequestError) || !error.definitelyRejected) throw error;
      assertCurrentReceipt(requestId);
      onObserve?.({ contentId, requestId, rejected: true });
      clearReceipt(requestId);
      if (!signal.aborted) setView({ phase: 'rejected', message: error.message });
      return null;
    }
  }

  async function monitor(initial: RegenerationJob, signal: AbortSignal, notify: boolean, requestId: string | null) {
    const until = Date.now() + POLL_WINDOW_MS;
    let job = initial;
    while (!signal.aborted) {
      assertCurrentReceipt(requestId);
      if (!['pending', 'processing', 'completed', 'failed'].includes(job.status)) throw new Error('Unrecognized regeneration job status');
      if (notify || awaitingCompletion.current === job.jobId || ['pending', 'processing'].includes(job.status)) {
        onObserve?.({ contentId, job });
      }
      setJobId(job.jobId);
      setView({ phase: job.status, message: job.error || job.progress?.message });
      if (job.status === 'completed' || job.status === 'failed') {
        clearReceipt(requestId);
        const refresh = notify || awaitingCompletion.current === job.jobId;
        awaitingCompletion.current = undefined;
        if (job.status === 'completed' && refresh) completion.current();
        return;
      }
      awaitingCompletion.current = job.jobId;
      notify = true;
      if (Date.now() >= until) {
        setView({ phase: job.status, paused: true, message: 'Automatic status checks paused. The job is still queued or running. Check its status to continue.' });
        return;
      }
      await pause(signal);
      if (signal.aborted) return;
      job = await readRegenerationJob(job.jobId, signal);
    }
  }

  async function observe(signal: AbortSignal, resubmitUnconfirmed: boolean) {
    const stored = receipt();
    if (stored) onObserve?.({ contentId, ...stored });
    // A newer receipt may still be awaiting admission. Resolve that request;
    // the panel's remembered job belongs only to receipt-free observation.
    const knownJobId = stored ? stored.jobId : awaitingCompletion.current;
    setJobId(knownJobId);
    const found = knownJobId
      ? await readRegenerationJob(knownJobId, signal)
      : await findRegenerationJob(contentId, stored?.requestId, signal);
    assertCurrentReceipt(stored?.requestId ?? null);
    if (found && (stored || ['pending', 'processing'].includes(found.status))) onObserve?.({ contentId, requestId: stored?.requestId, job: found });
    if (signal.aborted) return;
    if (found) {
      if (stored) save({ ...stored, jobId: found.jobId }, stored.requestId);
      await monitor(found, signal, !!stored, stored?.requestId ?? null);
    } else if (stored) {
      if (resubmitUnconfirmed) {
        // Same durable receipt, even if the first POST committed and its response was lost.
        const result = await submit(stored.requestId, signal);
        if (!result) return;
        assertCurrentReceipt(stored.requestId);
        onObserve?.({ contentId, requestId: stored.requestId, job: result.job });
        if (signal.aborted) return;
        save({ ...stored, jobId: result.jobId }, stored.requestId);
        await monitor(result.job, signal, true, stored.requestId);
      } else {
        setView({ phase: 'unconfirmed', message: 'No accepted job was found for this request. Regenerate explicitly retries the same request.' });
      }
    } else setView({ phase: 'idle' });
  }

  async function run(action: (signal: AbortSignal) => Promise<void>) {
    if (busy.current) return;
    busy.current = true;
    const own = new AbortController();
    controller.current = own;
    try { await action(own.signal); }
    catch (error) {
      if (!own.signal.aborted) setView({ phase: 'unavailable', message: error instanceof Error ? error.message : String(error) });
    } finally {
      if (controller.current === own) busy.current = false;
    }
  }

  useEffect(() => {
    busy.current = false;
    awaitingCompletion.current = undefined;
    setJobId(undefined);
    setView({ phase: 'checking' });
    void run(signal => observe(signal, false));
    return () => { controller.current?.abort(); };
    // A new scope cancels observation only. The persisted server job is never cancelled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  function start() {
    void run(async signal => {
      setView({ phase: 'submitting' });
      const current = receipt();
      if (current) return observe(signal, true);
      const next = { requestId: crypto.randomUUID() };
      save(next, null); // If persistence fails, refuse BEFORE any POST can be accepted.
      onObserve?.({ contentId, ...next });
      const result = await submit(next.requestId, signal);
      if (!result) return;
      assertCurrentReceipt(next.requestId);
      onObserve?.({ contentId, requestId: next.requestId, job: result.job });
      if (signal.aborted) return;
      save({ ...next, jobId: result.jobId }, next.requestId);
      await monitor(result.job, signal, true, next.requestId);
    });
  }

  const active = ['checking', 'submitting', 'pending', 'processing', 'unavailable'].includes(view.phase);
  const label = view.phase === 'pending' ? 'Regeneration queued' : view.phase === 'processing' ? 'Regeneration running'
    : view.phase === 'checking' ? 'Checking regeneration' : view.phase === 'submitting' ? 'Submitting regeneration'
      : view.phase === 'completed' ? 'Regeneration complete — ready for review'
        : view.phase === 'rejected' ? 'Regeneration request rejected'
        : view.phase === 'unconfirmed' ? 'Regeneration submission unconfirmed'
        : view.phase === 'failed' ? 'Regeneration failed' : view.phase === 'unavailable' ? 'Regeneration status unavailable' : '';
  return <div className="flex flex-col gap-2">
    {label && <div role="status" className="text-sm">{label}{view.message && <p>{view.message}</p>}</div>}
    {jobId && <p className="text-xs text-muted-foreground">Job {jobId}</p>}
    <div className="flex gap-2">
      <Button variant="outline" disabled={disabled || active} onClick={start}>Regenerate</Button>
      {(view.paused || ['unavailable', 'unconfirmed', 'rejected'].includes(view.phase)) && <Button variant="outline" onClick={() => void run(signal => observe(signal, false))}>Check existing request</Button>}
    </div>
  </div>;
}
